import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import VoiceInput from '../components/VoiceInput';
import AppShell from '../components/AppShell';
import { 
  Send, Sparkles, Loader2,
  Lightbulb, ArrowRight, Mic
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ActionRunCard from '../components/ActionRunCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DelphiPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [suggestions, setSuggestions] = useState([
    'Create a YouTube Short',
    'Research my competitors',
    'Create an image',
    "Plan next week's content",
    'How is my content performing?'
  ]);
  const [activeRuns, setActiveRuns] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Recover in-flight/recent work after refresh or navigation
    const loadRuns = async () => {
      try {
        const r = await axios.get(`${API}/delphi/runs?limit=3`, { headers: { Authorization: `Bearer ${token}` } });
        const recent = (r.data.runs || []).filter(x =>
          x.status === 'running' || x.status === 'queued' ||
          (x.completed_at && Date.now() - new Date(x.completed_at).getTime() < 30 * 60 * 1000));
        setActiveRuns(recent);
      } catch { /* silent */ }
    };
    if (token) loadRuns();
  }, [token]);

  // Auto-send prompt handed over from the dashboard hero input
  const autoSentRef = useRef(false);
  useEffect(() => {
    const starter = location.state?.prompt;
    if (starter && token && !autoSentRef.current) {
      autoSentRef.current = true;
      window.history.replaceState({}, '');
      handleSend(starter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, token]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/delphi/ask`,
        { message: messageText, conversation_id: conversationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { response: aiResponse, conversation_id, suggestions: newSuggestions,
              action_run_id, action_run_ids, actions } = response.data;

      setMessages(prev => [...prev, {
        role: 'assistant', content: aiResponse,
        action_run_ids: (action_run_ids?.length ? action_run_ids : (action_run_id ? [action_run_id] : [])),
        actions: actions || []
      }]);
      setConversationId(conversation_id);
      if (newSuggestions?.length) setSuggestions(newSuggestions);
    } catch (error) {
      toast.error('Delphi encountered an error. Please try again.');
      setMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setSuggestions([
      'Create a YouTube Short',
      'Research my competitors',
      'Create an image',
      "Plan next week's content",
      'How is my content performing?'
    ]);
  };

  return (
    <AppShell>
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full p-5 sm:p-7 lg:p-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-2">Ask Delphi</p>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                Delphi
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                Tell Delphi what you want done — it creates, researches and executes
              </p>
            </div>
            {messages.length > 0 && (
              <Button
                onClick={startNewChat}
                variant="outline"
                className="border-border"
                data-testid="new-chat-btn"
              >
                New Chat
              </Button>
            )}
          </div>

          {/* Chat Area */}
          <div className="flex-1 overflow-hidden">
            {messages.length === 0 ? (
              /* Empty State */
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/30 to-purple-500/30 flex items-center justify-center mb-6 animate-pulse-glow">
                  <img 
                    src="https://images.unsplash.com/photo-1761652661873-a08d8cb25b66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwYWJzdHJhY3QlMjB0ZWNobm9sb2d5JTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzY4Nzc2OTEwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Delphi Oracle"
                    className="w-20 h-20 rounded-full object-cover"
                  />
                </div>
                <h2 className="text-2xl font-bold mb-2">Hello, {user?.name?.split(' ')[0]}!</h2>
                <p className="text-muted-foreground mb-8 max-w-md">
                  I'm Delphi. Tell me what you want — I'll create it, research it, or get it done.
                </p>

                {/* Work still in progress or recently finished */}
                {activeRuns.length > 0 && (
                  <div className="w-full max-w-md mb-6 text-left" data-testid="active-runs-section">
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Delphi is working on</p>
                    {activeRuns.map(r => (
                      <ActionRunCard key={r.id} runId={r.id} token={token} onFollowUp={(t) => handleSend(t)} />
                    ))}
                  </div>
                )}

                {/* What Delphi Can Help With */}
                <div className="bg-secondary/30 rounded-2xl p-6 mb-8 max-w-lg w-full">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-primary" />
                    <span className="font-medium">What I can do for you:</span>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground text-left">
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-primary" />
                      Create videos, Shorts, images, music & voiceovers
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-primary" />
                      Research trends & competitors with live data
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-primary" />
                      Plan, schedule & publish your content
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-primary" />
                      Answer strategy questions anytime
                    </li>
                  </ul>
                </div>

                {/* Suggestions */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(suggestion)}
                      className="px-4 py-2 bg-secondary/50 hover:bg-secondary rounded-full text-sm transition-colors"
                      data-testid={`suggestion-btn-${i}`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Messages */
              <div className="h-[calc(100vh-300px)] overflow-y-auto space-y-4 pr-2">
                {messages.map((message, i) => (
                  <div
                    key={i}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    data-testid={`chat-message-${i}`}
                  >
                    <div className={`max-w-[80%] ${
                      message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'
                    }`}>
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-primary" />
                          <span className="text-xs text-primary font-medium">Delphi</span>
                        </div>
                      )}
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      {message.role === 'assistant' && message.action_run_ids?.map((rid) => (
                        <ActionRunCard key={rid} runId={rid} token={token} onFollowUp={(t) => handleSend(t)} />
                      ))}
                      {message.role === 'assistant' && message.actions?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {message.actions.map((a, j) => (
                            <Button key={j} size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => a.type === 'chip' ? handleSend(a.label)
                                : (a.type === 'navigate' && a.path && navigate(a.path))}
                              data-testid={`msg-action-${j}`}>
                              {a.label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="chat-bubble-assistant flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">Delphi is thinking...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />

                {/* Suggestions after response */}
                {!loading && suggestions.length > 0 && messages.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-4">
                    {suggestions.map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => handleSend(suggestion)}
                        className="px-3 py-1.5 bg-secondary/50 hover:bg-secondary rounded-full text-xs transition-colors"
                        data-testid={`follow-up-btn-${i}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="mt-4 pt-4 border-t border-border">
            {/* Voice Mode Banner */}
            {isVoiceMode && voiceTranscript && (
              <div className="mb-3 p-3 bg-primary/10 border border-primary/20 rounded-xl animate-fadeIn">
                <div className="flex items-center gap-2 text-sm">
                  <Mic className="w-4 h-4 text-primary animate-pulse" />
                  <span className="text-primary font-medium">Listening...</span>
                </div>
                <p className="text-sm text-zinc-300 mt-1">{voiceTranscript}</p>
              </div>
            )}
            
            <div className="flex gap-3">
              {/* Voice Input Button */}
              <VoiceInput
                onTranscript={(text) => {
                  setVoiceTranscript(text);
                  if (text) {
                    setIsVoiceMode(true);
                    setInput(text);
                  }
                }}
                onSend={(text) => {
                  setIsVoiceMode(false);
                  setVoiceTranscript('');
                  handleSend(text);
                }}
                disabled={loading}
                autoSend={false}
              />
              
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (isVoiceMode && !e.target.value.includes(voiceTranscript)) {
                    setIsVoiceMode(false);
                    setVoiceTranscript('');
                  }
                }}
                onKeyPress={handleKeyPress}
                placeholder={isVoiceMode ? "Edit your message or press Enter to send..." : "Ask Delphi anything..."}
                className="input-oracle flex-1"
                disabled={loading}
                data-testid="delphi-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="btn-oracle px-6"
                data-testid="delphi-send-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-3">
              <Mic className="w-3 h-3 inline mr-1" />
              Tap the microphone to speak, or type your question
            </p>
          </div>
        </div>
    </AppShell>
  );
};

export default DelphiPage;
